import { createRFQTOTAL } from "actions/supplierflow/supplierRfqActions";
import { getCookie, setCookie } from "utils/cookiesHelper";
import { logErrorIfDebug } from "utils/logging";
import { navigate } from "@reach/router";

import type { AppDispatch } from "newui/store";
import store from "newui/store";

import { getCompanyProfile } from "../Company/company-api";
import { getUserProfile, getUserToken } from "./auth-api";
import type { GuestSwitch } from "./authSlice";
import {
	authenticateTwoFactor,
	logoutUser,
	setCurrentCompany,
	setCurrentUser,
	setGuestSwitching,
	setIsAuthLoading,
} from "./authSlice";
import type { User } from "./types/User.type";
import type { UserCompanyProfile } from "./types/UserCompanyProfile.type";
import { isPAPLUsers } from "utils/helpers";
import { CompanyProfileType } from "./types/CompanyProfileType";
import { UserRoleName } from "./types/UserRole.type";
import { USER_ROLES_FOR_SELECTION } from "./constants/UserRoleConstants";
import { LabelValue } from "newui/types/LabelValue.type";
import { LOCAL_STORAGE_KEYS } from "newui/constants/localStorageKeys";
import { SupplierPaths } from "newui/utils/navigation/SupplierPaths";
import type { AuthenticateResponse } from "./types/AuthenticateResponse.type";

export interface TwoFactorChallengeSignal {
	twoFactorRequired: boolean;
	mfaToken?: string;
	availableMethods?: AuthenticateResponse["available_methods"];
}

/**
 * Detects the pending-2FA signal in a login response, handling BOTH transport
 * shapes the backend can return:
 *  - AuthService (member login) returns the fields at the TOP level.
 *  - GuestService wraps them in the `ApiResponseDTO` envelope under `data`.
 * The challenge branch is driven solely off this normalised, server-authoritative
 * signal — never re-derived client-side.
 */
export const extractTwoFactorChallenge = (
	payload: unknown
): TwoFactorChallengeSignal => {
	const raw = (payload ?? {}) as Record<string, unknown>;
	const envelope = (raw.data ?? {}) as Record<string, unknown>;
	const source = raw.two_factor_required !== undefined ? raw : envelope;

	return {
		twoFactorRequired: source.two_factor_required === true,
		mfaToken:
			typeof source.mfa_token === "string" ? source.mfa_token : undefined,
		availableMethods:
			source.available_methods as TwoFactorChallengeSignal["availableMethods"],
	};
};

export const setSwitchFromGuest = async (
	company?: UserCompanyProfile,
	user?: User,
	url?: string
) => {
	return store.dispatch(
		setGuestSwitching({
			is_loaded: false,
			company: company,
			user: user,
			url: url,
		} as GuestSwitch)
	);
};

export const reloadUser = async () => {
	const profileResponse = await getUserProfile();

	try {
		if (!profileResponse) {
			throw new Error("Failed to retrieve User Profile.");
		}

		const userProfile = profileResponse.data;

		const companyResponse = await getCompanyProfile(userProfile.company_id);

		if (!companyResponse) {
			throw new Error("Failed to retrieve Company Details.");
		}

		const companyProfile = companyResponse.data;
	} catch (exception) {
		logErrorIfDebug(exception);
	}
};

export const refreshLoadedCompanyProfile = async (
	companyProfile: UserCompanyProfile
) => {
	store.dispatch(setCurrentCompany(companyProfile));
};

export const refreshLoadedUserProfile = async (userProfile: User) => {
	setCookie("is_admin", userProfile.is_admin.toString());
	setCookie("company_admin", userProfile.company_admin.toString() || null);

	await store.dispatch(setCurrentUser(userProfile));

	setCookie("is_admin", userProfile?.is_admin.toString());
};

export const clearGuestUser = async () => {
	localStorage.removeItem("guest_user");
	await store.dispatch(logoutUser());
};

export const makeCompanyProfileFromGuest = async (
	company: UserCompanyProfile,
	user: User
) => {
	localStorage.removeItem("guest_user");
	await store.dispatch(createRFQTOTAL(1));
	await refreshLoadedUserProfile(user);
	await refreshLoadedCompanyProfile(company);
};

export const processAuthenticatedUser = async (
	user: User,
	dispatch: AppDispatch,
	enforceTwoFactor = true // default true for login
): Promise<boolean> => {
	// store 2FA enrolment info in Redux. The pending-login → challenge branch is
	// owned solely by the login flow (AuthContextProvider) off the
	// server-authoritative `two_factor_required` signal; this function only runs
	// for an already-authenticated user, so it no longer navigates to a 2FA
	// challenge or writes any client-side 2FA session flag.
	await dispatch(
		authenticateTwoFactor({
			two_factor_enabled: user.two_factor_enabled,
			two_factor_verified: false,
		})
	);

	if (enforceTwoFactor && isPAPLUsers(user)) {
		// Forced 2FA enrolment for PAPL users now lands on the new Settings →
		// Security enrolment flow (Task 005); the legacy `/two-factor-setup`
		// reach-router route has been removed.
		if (user.company_id != null) {
			navigate(new SupplierPaths(user.company_id).accountSettings.security);
		}
		return false;
	}

	// fetch full profile if missing (and not guest)
	if (!user.company && !user.is_guest) {
		const userProfileResponse = await getUserProfile();

		// TODO: need to do something here if we fail

		user = userProfileResponse?.data;
	}

	localStorage.setItem(
		LOCAL_STORAGE_KEYS.APP_MENU_STATE,
		JSON.stringify({ isCollapsed: false })
	);

	dispatch(setCurrentUser(user));

	return true;
};

export const getOrFetchXsrfToken = async (forceRefresh = false) => {
	const xsrfToken = getCookie("XSRF-TOKEN");

	if (xsrfToken && !forceRefresh) {
		return;
	}

	await getUserToken();
};

let fetchInProgress: Promise<boolean> | null = null;

export function fetchCurrentUserOnce(dispatch: AppDispatch): Promise<boolean> {
	if (fetchInProgress) {
		return fetchInProgress;
	}

	fetchInProgress = (async () => {
		dispatch(setIsAuthLoading(true));

		try {
			await getOrFetchXsrfToken();

			const authResponse = await getUserProfile();

			if (!authResponse.data) {
				return false;
			}
			const result = await processAuthenticatedUser(
				authResponse.data,
				dispatch,
				false
			);
			return result;
		} catch (error) {
			logErrorIfDebug("fetchCurrentUserOnce ERROR", error);
		} finally {
			dispatch(setIsAuthLoading(false));
		}
	})();

	return fetchInProgress;
}

export const getPlanTypeClientOrSupplier = (
	planName?: string
): CompanyProfileType => {
	if (planName?.toLowerCase().includes("procurer")) {
		return CompanyProfileType.CLIENT;
	} else if (planName?.toLowerCase().includes("bidder")) {
		return CompanyProfileType.SUPPLIER;
	}

	return CompanyProfileType.NONE;
};

export const getUserRoleByValue = (
	roleName: UserRoleName
): LabelValue<string> => {
	return USER_ROLES_FOR_SELECTION.find((role) => role.value === roleName);
};
